
import { useState, useEffect } from "react";
import { getCategories } from "@/services/hackService";
import { HackFilter } from "@/types";
import { Filter, X } from "lucide-react";

interface HackFiltersProps {
  filters: HackFilter;
  onFilterChange: (filters: HackFilter) => void;
}

const HackFilters = ({ filters, onFilterChange }: HackFiltersProps) => {
  const [categories, setCategories] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await getCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      }
    };
    
    fetchCategories();
  }, []);

  const handleCategoryChange = (category: string) => {
    onFilterChange({ ...filters, category });
  };

  const handleTestingStatusChange = (testingStatus: string) => {
    onFilterChange({ ...filters, testingStatus });
  };

  const clearFilters = () => {
    onFilterChange({ search: filters.search, category: "", testingStatus: "" });
  };

  const toggleFilters = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-4">
        <button 
          onClick={toggleFilters}
          className="flex items-center text-sm font-medium text-gray-700 hover:text-black"
        >
          <Filter size={16} className="mr-2" />
          {isOpen ? "Hide Filters" : "Show Filters"}
        </button>
        
        {(filters.category || filters.testingStatus) && (
          <button 
            onClick={clearFilters}
            className="flex items-center text-sm text-red-600 hover:text-red-800"
          >
            <X size={16} className="mr-1" />
            Clear Filters
          </button>
        )}
      </div>
      
      {isOpen && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-hackGreen-100 p-4 rounded-lg mb-6 animate-fade-in">
          <div>
            <h3 className="font-medium mb-2">Category</h3>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="category"
                  checked={!filters.category}
                  onChange={() => handleCategoryChange("")}
                  className="mr-2"
                />
                All Categories
              </label>
              {categories.map((category) => (
                <label key={category} className="flex items-center">
                  <input
                    type="radio"
                    name="category"
                    checked={filters.category === category}
                    onChange={() => handleCategoryChange(category)}
                    className="mr-2"
                  />
                  {category}
                </label>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="font-medium mb-2">Testing Status</h3>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="testingStatus"
                  checked={!filters.testingStatus}
                  onChange={() => handleTestingStatusChange("")}
                  className="mr-2"
                />
                All
              </label>
              {["User-tested", "OT-tested", "Not tested"].map((status) => (
                <label key={status} className="flex items-center">
                  <input
                    type="radio"
                    name="testingStatus"
                    checked={filters.testingStatus === status}
                    onChange={() => handleTestingStatusChange(status)}
                    className="mr-2"
                  />
                  {status}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HackFilters;
